// Forgot password form component
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import authService from '../../services/authService';
import { useLanguage } from '../../contexts/LanguageContext';
import './AuthForms.css';

function ForgotPasswordForm({ onSwitchToLogin }) {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetData, setResetData] = useState(null);
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError(t('auth.validationEmailRequired'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('auth.validationEmailInvalid'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const data = await authService.requestPasswordReset(email);
      setResetData(data);
      setIsSubmitted(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToken = () => {
    if (resetData?.resetToken) {
      navigator.clipboard.writeText(resetData.resetToken);
      alert(t('auth.resetTokenCopied'));
    }
  };

  if (isSubmitted) {
    return (
      <div className="auth-form-container">
        <div className="auth-form">
          <div className="auth-logo-container">
            <Link to="/">
              <img 
                src="/images/taroca_logo.png" 
                alt="Taroca Logo" 
                className="auth-logo"
              />
            </Link>
          </div>
          <div className="auth-success-message">
            <h2 className="auth-form-title">{t('auth.checkEmailTitle')}</h2>
            <p className="auth-form-subtitle">
              {resetData?.message || t('auth.checkEmailMessage')}
            </p>
            
            <div className="email-sent-info">
              <p className="auth-form-text">
                {t('auth.checkEmailMessage')}
              </p>
              <p className="auth-form-text small">
                {t('auth.resetLinkExpiry', 'The link will expire in 15 minutes for security reasons.')}
              </p>
              
              {/* Show token only in development if email fails */}
              {resetData?.resetToken && (
                <div className="reset-token-display development-only">
                  <p className="auth-form-text">
                    <strong>Development Mode - Reset Token:</strong>
                  </p>
                  <div className="token-box">
                    <code className="reset-token">{resetData.resetToken}</code>
                    <button 
                      type="button" 
                      onClick={copyToken} 
                      className="copy-token-btn"
                      title="Copy token"
                    >
                      📋
                    </button>
                  </div>
                  <Link 
                    to={`/reset-password?token=${resetData.resetToken}`} 
                    className="auth-submit-btn reset-link-btn"
                  >
                    Go to Reset Password Page
                  </Link>
                </div>
              )}
            </div>
            
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="auth-link-btn back-to-login-btn"
            >
              ← {t('auth.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form-container">
      <div className="auth-form">
        <div className="auth-logo-container">
          <Link to="/">
            <img 
              src="/images/taroca_logo.png" 
              alt="Taroca Logo" 
              className="auth-logo"
            />
          </Link>
        </div>
        <h2 className="auth-form-title">{t('auth.forgotPasswordTitle')}</h2>
        <p className="auth-form-subtitle">
          {t('auth.forgotPasswordSubtitle')}
        </p>

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form-fields">
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('auth.email')}
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              required
              className="form-input"
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !email.trim()}
            className="auth-submit-btn"
          >
            {isLoading ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
          </button>
        </form>

        <div className="auth-form-footer">
          <div className="auth-switch">
            <span>{t('auth.rememberPassword')}</span>
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="auth-link-btn"
            >
              {t('auth.signInLink')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordForm;
